import { useEffect, useRef } from 'react';
import type { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';

interface UseClearFieldErrorOnValueChangeOptions<TFieldValues extends FieldValues> {
  errorTypes?: readonly string[];
  fieldNames: ReadonlyArray<FieldPath<TFieldValues>>;
  form: UseFormReturn<TFieldValues>;
  onClear: () => void;
  shouldClear?: () => boolean;
  values: readonly unknown[];
}

const useClearFieldErrorOnValueChange = <TFieldValues extends FieldValues>({
  errorTypes,
  fieldNames,
  form,
  onClear,
  shouldClear,
  values,
}: UseClearFieldErrorOnValueChangeOptions<TFieldValues>) => {
  const previousValuesRef = useRef(values);

  // Callers pass watched-value tuples inline, so compare elements instead of array identity.
  useEffect(() => {
    const valuesDidChange = values.some((value, index) => previousValuesRef.current[index] !== value);
    previousValuesRef.current = values;

    if (!valuesDidChange) {
      return;
    }

    if (shouldClear && !shouldClear()) {
      return;
    }

    const hasTrackedError = fieldNames.some(fieldName => {
      const fieldErrorType = form.getFieldState(fieldName).error?.type;

      if (!fieldErrorType) {
        return false;
      }

      return errorTypes ? errorTypes.includes(fieldErrorType) : true;
    });

    if (!hasTrackedError) {
      return;
    }

    onClear();
  }, [errorTypes, fieldNames, form, onClear, shouldClear, values]);
};

export default useClearFieldErrorOnValueChange;
